import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiClient';

const ConfirmEmail: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    const confirmEmail = async () => {
      try {
        const confirmation = searchParams.get('confirmation');

        if (!confirmation) {
          setStatus('error');
          setMessage('Confirmation code not found. Please use the link from your email.');
          return;
        }

        // Call the Strapi confirmation endpoint
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.AUTH.EMAIL_CONFIRMATION}?confirmation=${confirmation}`,
          {
            method: 'GET',
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          setStatus('error');
          setMessage(
            errorData.message?.message ||
              errorData.error?.message ||
              'Email verification failed. The link may have expired.'
          );
          return;
        }

        setStatus('success');
        setMessage('Your email has been verified successfully! You can now log in.');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error: any) {
        console.error('Email confirmation error:', error);
        setStatus('error');
        setMessage(error.message || 'An error occurred during email verification.');
      }
    };

    confirmEmail();
  }, [searchParams, navigate]);

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: status === 'success' ? '#4caf50' : status === 'error' ? '#f44336' : '#1976d2',
            mb: 2,
          }}
        >
          {status === 'loading' && <CircularProgress size={32} sx={{ color: 'white' }} />}
          {status === 'success' && <CheckCircleOutlineIcon sx={{ color: 'white', fontSize: 32 }} />}
          {status === 'error' && <ErrorOutlineIcon sx={{ color: 'white', fontSize: 32 }} />}
        </Box>

        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          {status === 'success' && 'Email Verified'}
          {status === 'error' && 'Verification Failed'}
          {status === 'loading' && 'Verifying Email'}
        </Typography>

        <Alert severity={status === 'success' ? 'success' : status === 'error' ? 'error' : 'info'} sx={{ width: '100%', mb: 2 }}>
          {message}
        </Alert>

        {status === 'success' && (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
              You will be redirected to the login page in a few seconds...
            </Typography>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate('/login')}
              sx={{ mb: 2 }}
            >
              Go to Login Now
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3, textAlign: 'center' }}>
              Please try signing up again or contact support if the problem persists.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate('/signup')}
              >
                Sign Up Again
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </Box>
          </>
        )}

        {status === 'loading' && (
          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
            Please wait while we verify your email address...
          </Typography>
        )}
      </Paper>
    </Container>
  );
};

export default ConfirmEmail;
